/**
 * Convert any thrown error (axios, plain Error, string, unknown) into a short,
 * user-readable sentence suitable for a toast.
 *
 * Always logs the raw error to the console for dev visibility — never shows
 * stack traces or low-level axios messages to the user.
 */

const STACK_MARKERS = [
  /^Error:\s*/i,
  /^Traceback[\s\S]*$/i,
  /\bat\s+[\w$<>.]+\s*\(.*\)/g,
];

const cleanDetail = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  let s = raw.trim();
  STACK_MARKERS.forEach((re) => {
    s = s.replace(re, "").trim();
  });
  if (s.length > 140) s = s.slice(0, 137).trimEnd() + "...";
  return s;
};

const extractDetail = (data) => {
  if (!data) return "";
  if (typeof data === "string") return cleanDetail(data);
  if (typeof data !== "object") return "";

  // FastAPI / DRF style
  if (typeof data.detail === "string") return cleanDetail(data.detail);
  if (Array.isArray(data.detail) && data.detail.length) {
    const first = data.detail[0];
    if (typeof first === "string") return cleanDetail(first);
    if (first && typeof first.msg === "string") return cleanDetail(first.msg);
  }
  if (typeof data.message === "string") return cleanDetail(data.message);
  if (typeof data.error === "string") return cleanDetail(data.error);

  return "";
};

export const humanizeError = (err, fallback = "Something went wrong. Please try again.") => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error("[humanizeError]", err);
  }

  if (!err) return fallback;

  // Plain string
  if (typeof err === "string") return cleanDetail(err) || fallback;

  // Axios-style error
  const status = err?.response?.status;
  const detail = extractDetail(err?.response?.data);

  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return "We couldn't reach the server. Please try again.";
  }

  if (status === 401 || status === 403) {
    return "Your session expired. Please log in again.";
  }
  if (status === 404) {
    return "We couldn't find what you were looking for.";
  }
  if (status === 413) {
    return "That file is too large.";
  }
  if (status === 409) {
    return detail || "That action conflicts with existing data.";
  }
  if (status >= 400 && status < 500) {
    return detail || "Please check the entered information and try again.";
  }
  if (status >= 500) {
    return "We couldn't reach the server. Please try again.";
  }

  // Plain Error
  if (err?.message && typeof err.message === "string") {
    const cleaned = cleanDetail(err.message);
    if (cleaned && !/network|axios|xhr/i.test(cleaned)) return cleaned;
  }

  return fallback;
};

export default humanizeError;
